import { Link, useLocation } from 'react-router-dom';
import { Archive, ArchiveRestore, Ban, Copy, MoreHorizontal, Pencil, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { archiveOrder, cancelOrder, dismissOrderWontOrder, reopenOrder, type OrderDetail } from '@/api/orders';
import { useAuth } from '@/auth/useAuth';
import type { NextAction } from '../orderPresentation';
import { RunButtons } from '@/features/runs/RunButtons';

/**
 * The journey's controls, in one place: the primary button is the computed
 * next action and goes where that action is done; everything else — rename,
 * copy id, the lifecycle exits — sits in one overflow menu. Two write codes
 * meet here (docs/PERMISSIONS.md): renaming is a details edit
 * (`orders:write:journeys`); Mark ordered and the lifecycle exits change the
 * journey's status (`orders:write:lifecycle`). Each item shows only with its
 * own code, and the menu only when it would hold something.
 */
export function JourneyActions({ detail, next, onMarkOrdered, onEditName, onTab, onChanged, onLeft }: {
  detail: OrderDetail;
  next: NextAction;
  onMarkOrdered: () => void;
  onEditName: () => void;
  onTab: (tab: 'receiving') => void;
  /** The journey changed in place. */
  onChanged: () => void;
  /** The journey left the list (archived, cancelled, dismissed). */
  onLeft: () => void;
}) {
  const location = useLocation();
  const { can } = useAuth();
  const canEditDetails = can('orders:write:journeys');
  const canChangeStatus = can('orders:write:lifecycle');
  const returnTo = { returnTo: location.pathname + location.search };

  const canWontOrder = canChangeStatus && detail.status === 'announced' && !detail.archivedAt;
  const canCancel = canChangeStatus && !detail.archivedAt
    && ['placed', 'confirmed', 'invoiced', 'partially_shipped', 'shipped'].includes(detail.status);
  const canArchive = canChangeStatus && !detail.archivedAt && detail.status !== 'announced';
  const canReopen = canChangeStatus && (Boolean(detail.archivedAt) || detail.status === 'wont_order');
  const anyExit = canWontOrder || canCancel || canArchive || canReopen;

  const run = (fn: () => Promise<void>, after: () => void) => async () => {
    try { await fn(); after(); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'The change could not be saved'); }
  };

  const copyId = async () => {
    try { await navigator.clipboard.writeText(detail.id); toast.success('Journey ID copied'); }
    catch { toast.error('Could not copy the journey ID'); }
  };

  let primary: React.ReactNode = null;
  if (next.verb) {
    if (next.code === 'order') {
      primary = canChangeStatus ? <Button onClick={onMarkOrdered}>{next.verb}</Button> : null;
    } else if (next.tab) {
      primary = <Button onClick={() => onTab(next.tab!)}>{next.verb}</Button>;
    } else if (next.to) {
      primary = <Button asChild><Link to={next.to} state={returnTo}>{next.verb}</Link></Button>;
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!detail.archivedAt && <RunButtons subject={{ orderId: detail.id }} size="default" />}
      {primary}
      {(canEditDetails || anyExit) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More"><MoreHorizontal /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {canEditDetails && <DropdownMenuItem onSelect={onEditName}><Pencil /> Edit name</DropdownMenuItem>}
            <DropdownMenuItem onSelect={() => void copyId()}><Copy /> Copy journey ID</DropdownMenuItem>
            {anyExit && <DropdownMenuSeparator />}
            {canWontOrder && (
              <DropdownMenuItem className="text-danger-ink" onSelect={run(() => dismissOrderWontOrder(detail.id), onLeft)}>
                <Ban /> Won't order
              </DropdownMenuItem>
            )}
            {canCancel && (
              <DropdownMenuItem className="text-danger-ink" onSelect={run(() => cancelOrder(detail.id), onLeft)}>
                <XCircle /> Cancelled
              </DropdownMenuItem>
            )}
            {canArchive && (
              <DropdownMenuItem onSelect={run(() => archiveOrder(detail.id), onLeft)}>
                <Archive /> Archive
              </DropdownMenuItem>
            )}
            {canReopen && (
              <DropdownMenuItem onSelect={run(() => reopenOrder(detail.id), onChanged)}>
                <ArchiveRestore /> Reopen
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
